import { Button, Checkbox } from '@upmc/ims.component-library';
import FormWrapper from 'components/Form/FormWrapper';
import {
	ERROR_MESSAGE_IMPERSONATOR,
	ERROR_MESSAGE_IMPERSONATOR_UNKNOWN,
} from 'components/Navigation/AppBar/ImpersonationIcon/_constants';
import { UserContext } from 'context/User/UserContext';
import { ERROR_MESSAGE_USER } from 'context/User/UserProvider';
import {
	Fragment,
	ReactElement,
	useCallback,
	useContext,
	useEffect,
	useMemo,
	useRef,
	useState,
} from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { Link, useHistory, useLocation } from 'react-router-dom';
import { getLegacyPath } from 'routes/_utils';
import Yup from 'utils/validation/yup-extensions';
import { LABEL_POSITIONS } from '../../../components/Form/_constants';
import {
	raiseErrorBanner,
	raiseInformationalBanner,
	raiseWarningBanner,
} from '../../../components/MessageDispatch/messageDispatch';
import { HomeButton } from '../../../components/UI/Input/Button/index';
import { UserType } from '../../../components/UI/Input/Custom/UserType/UserType';
import { ActionButtons, Actions } from '../../../components/UI/MDC/Card/Card';
import { divider as Divider } from '../../../components/UI/MDC/Divider/Divider';
import Radio from '../../../components/UI/MDC/Radio/Radio';
import RadioSet from '../../../components/UI/MDC/Radio/RadioSet/RadioSet';
import { HelperText } from '../../../components/UI/MDC/TextField/TextField';
import {
	Headline5,
	Headline6,
	Subtitle2,
} from '../../../components/UI/MDC/Typography/Typography';
import { RequestTypes as REQUEST_TYPES } from '../../../constants/enums';
import * as REQUEST_PARAMS from '../../../constants/requestConstants';
import StyleAuxiliary from '../../../hoc/Auxiliary/StyleAuxiliary/StyleAuxiliary';
import WithLoadingHandler from '../../../hoc/withLoadingHandler/withLoadingHandler';
import { clearMessages } from '../../../store/ancillary/actions';
import { createRequestee } from '../../../store/requestFlow/_utility';
import { advance, init } from '../../../store/requestFlow/actions';
import { setSession } from '../../../store/utility';
import { useQuery } from '../../../utils/hooks/useQuery';
import Base from '../../Base/Base';
import { RequesteeSelections } from '../RequesteeSearch/RequesteeSelections/RequesteeSelections';
import { CELL_KEYS, HEADERS } from '../RequesteeSearch/_constants';
import classes from './Agreements.module.scss';
import StaffAccessAgreement from './StaffAccessAgreement/StaffAccessAgreement';
import {
	FORM_KEYS as STAFF_ACCESS_AGREEMENT_FORM_KEYS,
	FORM_INITIAL_VALUES as STAFF_ACCESS_AGREEMENT_INITIAL_VALUES,
	FORM_VALIDATION_SCHEMA as STAFF_ACCESS_AGREEMENT_VALIDATION_SCHEMA,
} from './StaffAccessAgreement/_constants';
import { SurveyorGuidelineAcknowledgment } from './SurveyorGuidelineAcknowledgment/SurveyorGuidelineAcknowledgment';
import {
	FORM_KEYS as SURVEYOR_FORM_KEYS,
	FORM_INITIAL_VALUES as SURVEYOR_INITIAL_VALUES,
	FORM_VALIDATION_SCHEMA as SURVEYOR_VALIDATION_SCHEMA,
} from './SurveyorGuidelineAcknowledgment/_constants';
import {
	getIPAddress,
	getNewRequesteeByTrackingNumber,
	getNewRequesteesByTrackingNumber,
	getRequesteeRowByID,
	getTrackingNumber,
	getUserType,
	postNoComputerAccess,
	postStaffAccessAgreement,
	postStaffAccessAgreementServiceAccount,
	postSurveyorGuidelines,
	postSystemAccessAgreement,
	postUserType,
} from './_api';
import { postAppAdd } from '../Requests/_api';
import {
	CSS_CLASSES,
	FORM_KEYS,
	SPONSOR_ACKNOWLEDGMENT_SCHEMA_OBJECT,
	TENANT_BADGE_ONLY_INITIAL_VALUES,
	TENANT_BADGE_ONLY_VALIDATION_SCHEMA_OBJECT,
	USER_TYPES,
} from './_constants';
import { isMultiUserRequest } from './_utilities';
import { HomeAddress } from './components/AddressGroup/HomeAddressGroup/HomeAddress';
import { AlternateEmailTextBox } from './components/AlternateEmailTextBox/AlternateEmailTextBox';
import { DepartmentSearchBox } from './components/DepartmentSearchBox/DepartmentSearchBox';
import { HospitalDropdown } from './components/HospitalDropdown/HospitalDropdown';
import { OrganizationSearchBox } from './components/OrganizationSearchBox/OrganizationSearchBox';
import {
	FORM_KEYS as ORGANIZATION_FORM_KEYS,
	FORM_INITIAL_VALUES as ORGANIZATION_INITIAL_VALUES,
	FORM_VALIDATION_SCHEMA_OBJECT as ORGANIZATION_VALIDATION_SCHEMA_OBJECT,
} from './components/OrganizationSearchBox/_constants';
import { SponsoringDepartmentDropdown } from './components/SponsoringDepartmentDropdown/SponsoringDepartmentDropdown';

/**
 * Functional component for individual, bulk handling and enforcing compliance to computer access policies
 * on non-employee, contingent worker creation and application request requestees. Bulk processing exclusively reserved for vendor associations,
 * all other policies enforced individually.
 *
 * @returns {ReactElement} rendered component.
 */
const Agreements = () => {
	const dispatch = useDispatch();
	const location = useLocation();
	const history = useHistory();
	/**
	 * @type {import('context/User/UserContext').ContextUser}
	 */
	const contextUser = useContext(UserContext);
	const user = contextUser.userInfo.allowImpersonation
		? (contextUser.userInfo.impersonationProfile ??
			contextUser.userInfo.userProfile)
		: contextUser.userInfo.userProfile;

	//#region Query parameters
	/**
	 * Type of application request specified in URL query parameter(s).
	 * @type {string}
	 */
	const requestType = useQuery().get(REQUEST_PARAMS.REQUEST_TYPE);
	/**
	 * Optional user type of non-employee specified in URL query parameter(s).
	 * @type {string}
	 */
	const userType = useQuery().get(REQUEST_PARAMS.USER_TYPE);
	//#endregion

	//#region Redux
	/**
	 * Triggers, indicates status of page submission.
	 * @type {boolean}
	 */
	const rAdvance = useSelector(state => state.requestFlow.advance);
	/**
	 * Nullable form values passed to submission.
	 * @type {object}
	 */
	const rForm = useSelector(state => state.requestFlow.form);
	/**
	 * Toggles base page loading.
	 * @type {boolean}
	 */
	const rLoading = useSelector(state => state.requestFlow.loading);
	/**
	 * Array of message object(s) used to handle users attempting staff access agreement
	 * acknowledgments without Network access.
	 * @type {object[]}
	 */
	const rMessages = useSelector(state => state.ancillary.messages);
	/**
	 * Array of requestee object(s).
	 * @type {object[]}
	 */
	const rRequestees = useSelector(state =>
		state.requestFlow.requestees ? state.requestFlow.requestees : [],
	);
	//#endregion

	//#region Refs
	/**
	 * Persistent, mutable ref object representing whether component has mounted.
	 * @type {Object}
	 */
	const refMounted = useRef(false);
	//#endregion

	//#region Local State
	/**
	 * Local state indication whether request possesses (selected) requestee(s).
	 * @type {boolean}
	 */
	let sHasRequestees = true;
	/**
	 * Setter for selected requestee indication.
	 * @type {Function}
	 * @param {boolean} state updated local state value.
	 * @returns void
	 */
	let setHasRequestees;
	[sHasRequestees, setHasRequestees] = useState(true);
	/**
	 * Local state indication whether request includes computer access.
	 * @type {boolean}
	 */
	let sIsComputerAccess = true;
	/**
	 * Setter for computer access request indication.
	 * @type {Function}
	 * @param {boolean} state updated local state value.
	 * @returns void
	 */
	let setIsComputerAccess;
	[sIsComputerAccess, setIsComputerAccess] = useState(true);
	/**
	 * Local state conditionally possessing the client's IP address.
	 * @type {string}
	 */
	let sIPAddress;
	/**
	 * Setter for client IP address.
	 * @type {Function}
	 * @param {string} state updated local state value.
	 * @returns void
	 */
	let setIPAddress;
	[sIPAddress, setIPAddress] = useState('');
	/**
	 * Local state indication whether requestee is a new non-employee of type "Regulatory User".
	 * @type {boolean}
	 */
	let sIsRegulatoryUser;
	/**
	 * Setter for new "Regulatory User" non-employee indication.
	 * @type {Function}
	 * @param {boolean} state updated local state value.
	 * @returns void
	 */
	let setIsRegulatoryUser;
	[sIsRegulatoryUser, setIsRegulatoryUser] = useState(false);
	/**
	 * Local state indication whether request preconditions staff access agreement.
	 * @type {boolean}
	 */
	let sIsStaffAccessAgreement;
	/**
	 * Setter for staff access agreement indication.
	 * @type {Function}
	 * @param {boolean} state updated local state value.
	 * @returns void
	 */
	let setIsStaffAccessAgreement;
	[sIsStaffAccessAgreement, setIsStaffAccessAgreement] = useState(false);
	/**
	 * Local state indication whether requestee is a non-employee of type "Tenant Badge Only", both new and existing.
	 * @type {boolean}
	 */
	let sIsTenantBadgeOnly;
	/**
	 * Setter for "Tenant Badge Only" non-employee indication.
	 * @type {Function}
	 * @param {boolean} state updated local state value.
	 * @returns void
	 */
	let setIsTenantBadgeOnly;
	[sIsTenantBadgeOnly, setIsTenantBadgeOnly] = useState(false);
	/**
	 * Local state indication whether request preconditions vendor association.
	 * @type {boolean}
	 */
	let sIsVendorAssociation;
	/**
	 * Setter for vendor association indication.
	 * @type {Function}
	 * @param {boolean} state updated local state value.
	 * @returns void
	 */
	let setIsVendorAssociation;
	[sIsVendorAssociation, setIsVendorAssociation] = useState(false);
	/**
	 * Local state conditionally possessing request tracking number provided in URL query parameter.
	 * @type {string}
	 */
	let sTrackingNumber;
	/**
	 * Setter for request tracking number.
	 * @type {Function}
	 * @param {string} state updated local state value.
	 * @returns void
	 */
	let setTrackingNumber;
	[sTrackingNumber, setTrackingNumber] = useState(
		useQuery().get(REQUEST_PARAMS.TRACKING_NUMBER),
	);
	/**
	 * Setter for selected cost center.
	 * @type {Function}
	 * @param {string} state updated local state value.
	 * @returns void
	 */
	const [departmentId, setDepartmentId] = useState('');
	//#endregion

	//#region Constants
	const userTypeExclusions = useMemo(
		() => [USER_TYPES.TENANT_BADGE_ONLY],
		[],
	);
	const formikInitialValues = useMemo(() => {
		return {
			...(sIsVendorAssociation && {
				...ORGANIZATION_INITIAL_VALUES,
				[`${FORM_KEYS.SPONSOR_ACKNOWLEDGMENT}`]: false,
			}),
			...(sIsTenantBadgeOnly &&
				requestType !== REQUEST_TYPES.NON_EMPLOYEE &&
				TENANT_BADGE_ONLY_INITIAL_VALUES),
			...(sIsStaffAccessAgreement &&
				STAFF_ACCESS_AGREEMENT_INITIAL_VALUES),
			...(sIsRegulatoryUser && SURVEYOR_INITIAL_VALUES),
		};
	}, [
		sIsVendorAssociation,
		sIsTenantBadgeOnly,
		requestType,
		sIsStaffAccessAgreement,
		sIsRegulatoryUser,
	]);
	const formikValidationSchema = useMemo(
		() =>
			Yup.object().shape({
				...(sIsVendorAssociation && {
					...ORGANIZATION_VALIDATION_SCHEMA_OBJECT,
					...SPONSOR_ACKNOWLEDGMENT_SCHEMA_OBJECT,
				}),
				...(sIsTenantBadgeOnly &&
					requestType !== REQUEST_TYPES.NON_EMPLOYEE &&
					TENANT_BADGE_ONLY_VALIDATION_SCHEMA_OBJECT),
				...(sIsStaffAccessAgreement &&
					STAFF_ACCESS_AGREEMENT_VALIDATION_SCHEMA),
				...(sIsRegulatoryUser && SURVEYOR_VALIDATION_SCHEMA),
			}),
		[
			sIsVendorAssociation,
			sIsTenantBadgeOnly,
			requestType,
			sIsStaffAccessAgreement,
			sIsRegulatoryUser,
		],
	);
	const onSubmit = useCallback(
		async (values, actions) => {
			dispatch(clearMessages());
			dispatch(advance(values));
			actions.setSubmitting(false); // submission continued outside of Formik
		},
		[dispatch],
	);
	//#endregion

	//#region Lifecycle Hooks
	/**
	 * Initial render lifecycle hook. Resets Redux state.
	 *
	 * @function
	 * @return void. No clean-up performed.
	 */
	useEffect(() => {
		dispatch(
			init({
				advance: false,
				loading: true,
			}),
		);
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, []);

	/**
	 * Lifecycle hook on local variable change. Evaluates and takes action on logged-on user information once available during initial loading.
	 *
	 * @function
	 * @return void. No clean-up performed.
	 */
	useEffect(() => {
		/**
		 * Evaluates type of request, existence of request
		 * tracking number, policy violations by requestee(s) if non-null logged-on user information available.
		 *
		 * @function
		 * @return void.
		 */
		async function render() {
			if (user.ID && rLoading && !rAdvance) {
				// local variables to avoid race conditions
				// Redux state race(s)
				let shouldAdvance = false;
				let requestee = null;
				let row = null;
				// local state race(s)
				let isRegulatoryUser = false;
				let isTenantBadgeOnly = false;
				let isStaffAccessAgreement = false;
				let isVendorAssociation = false;

				try {
					// legacy session support
					let requestMode = 1;

					// tracking number generation if non-existing
					if (!sTrackingNumber) {
						const trackingNumber = await getTrackingNumber(
							isMultiUserRequest(requestType),
						);
						setTrackingNumber(trackingNumber);
						await setSession('pTrackingNo', 'P' + trackingNumber);
						await setSession('newTrackingNumMod', trackingNumber);
					}

					switch (requestType) {
						case REQUEST_TYPES.MULTI_USER:
						case REQUEST_TYPES.MULTI_SERVICE_ACCOUNT:
							//check if all requestees are employees
							const allEmployees = rRequestees.every(
								requestee => {
									if (requestee.isSelected) {
										const requesteeKeys =
											Object.keys(requestee);
										return (
											requesteeKeys.includes(
												'Employee_Status',
											) &&
											requestee[
												'Employee_Status'
											].Value.toLowerCase() === 'true'
										);
									} else {
										return true;
									}
								},
							);

							if (!allEmployees) {
								//check on vendor association requests
								isVendorAssociation = rRequestees.some(
									requestee => {
										if (requestee.isSelected) {
											const requesteeKeys =
												Object.keys(requestee);
											return requesteeKeys.includes(
												CELL_KEYS.UNASSOCIATED,
											);
										} else {
											return false;
										}
									},
								);
								setIsVendorAssociation(isVendorAssociation);
							}

							//advance or continue with state setting
							if (allEmployees || !isVendorAssociation) {
								dispatch(advance());
								shouldAdvance = true;
							}
							break;
						case REQUEST_TYPES.MULTI_USER_NON_EMPLOYEE:
							await getNewRequesteesByTrackingNumber(
								sTrackingNumber,
							)
								.then(async rows => {
									if (rows) {
										const trackingNumber =
											await getTrackingNumber(true);
										setTrackingNumber(trackingNumber);

										dispatch(
											init({
												requestees: rows.map(row =>
													createRequestee(row),
												),
												rows: [rows],
											}),
										);
										if (
											rows.every(row => {
												const requesteeKeys =
													Object.keys(row.Cells);

												const isVendorAssociation =
													requesteeKeys.includes(
														CELL_KEYS.UNASSOCIATED,
													);
												return !isVendorAssociation;
											})
										) {
											dispatch(advance());
											shouldAdvance = true;
										} else {
											setIsVendorAssociation(true);
										}
									}
								})
								.catch(error => {
									throw error;
								});
							break;
						case REQUEST_TYPES.NON_EMPLOYEE:
							requestMode = 2;
							await getNewRequesteeByTrackingNumber(
								sTrackingNumber,
							)
								.then(async row => {
									if (row) {
										dispatch(
											init({
												requestees: [
													createRequestee(row),
												],
												rows: [row],
											}),
										);
										const requesteeKeys = Object.keys(
											row.Cells,
										);
										if (
											requesteeKeys.includes(
												CELL_KEYS.RESTRICTED,
											)
										) {
											isRegulatoryUser = true;
											setIsRegulatoryUser(
												isRegulatoryUser,
											);
										} else if (
											requesteeKeys.includes(
												CELL_KEYS.DENIED,
											)
										) {
											isTenantBadgeOnly = true;
											setIsTenantBadgeOnly(
												isTenantBadgeOnly,
											);
											setIsComputerAccess(false);
											dispatch(advance());
											shouldAdvance = true;
										} else {
											// all non-employee requests require some action if proceeding to computer access
											isVendorAssociation = true;
											setIsVendorAssociation(
												isVendorAssociation,
											);
										}
									}
								})
								.catch(error => {
									throw error;
								});
							break;

						case REQUEST_TYPES.SELF:
						case REQUEST_TYPES.SELF_SERVICE_ACCOUNT:
							// create from logged-on user
							row = await getRequesteeRowByID(user.ID);
							requestee = createRequestee(row);
							dispatch(
								init({
									requestees: [requestee],
									rows: [row],
								}),
							);
						// intentionally fall through to single-user processing
						case REQUEST_TYPES.FOR_OTHERS_SERVICE_ACCOUNT:
						case REQUEST_TYPES.SINGLE:
							// if not self request
							if (requestee === null) {
								// check routing state
								if (location && location.state) {
									requestee = createRequestee(location.state);
									dispatch(
										init({
											requestees: [requestee],
											rows: [location.state],
										}),
									);
								}
								// default to Redux store
								else {
									requestee = rRequestees[0];
								}
							}
							const requesteeKeys = Object.keys(requestee);
							const requesteeID = requestee['FI_ID'].Value;
							const isSelf = user.ID.toString() === requesteeID;

							// advance if not non-employee
							if (
								requesteeKeys.includes('Employee_Status') &&
								requestee[
									'Employee_Status'
								].Value.toLowerCase() === 'true'
							) {
								dispatch(advance());
								shouldAdvance = true;
							} else {
								setIPAddress(await getIPAddress());

								const userType = await getUserType(requesteeID);
								if (userType) {
									switch (userType.toLowerCase()) {
										case USER_TYPES.TENANT_BADGE_ONLY:
											isTenantBadgeOnly = true;
											setIsTenantBadgeOnly(
												isTenantBadgeOnly,
											);
											break;
										case USER_TYPES.REGULATORY_USER:
											shouldAdvance = true;
											dispatch(advance());
											break;
										default:
											break;
									}
								}
								isVendorAssociation = requesteeKeys.includes(
									CELL_KEYS.UNASSOCIATED,
								);
								setIsVendorAssociation(isVendorAssociation);
								isStaffAccessAgreement =
									requesteeKeys.includes(CELL_KEYS.PENDING) &&
									isSelf;
								setIsStaffAccessAgreement(
									isStaffAccessAgreement,
								);

								// advance if no policy violations
								if (
									!isRegulatoryUser &&
									!isTenantBadgeOnly &&
									!isStaffAccessAgreement &&
									!isVendorAssociation
								) {
									dispatch(advance());
									shouldAdvance = true;
								}
							}
							break;
						default:
							break;
					}
					await setSession('Request_AppMode', requestMode);
					// ref must be written to inside `try` block, not `finally`
					refMounted.current = !shouldAdvance
						? true
						: refMounted.current;
				} catch (error) {
					raiseErrorBanner(
						error.cause ? error.cause : error,
						dispatch,
					);
				} finally {
					if (!shouldAdvance) {
						dispatch(
							init({
								loading: false,
							}),
						);
					}
				}
			}
		}
		render();
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [user, rLoading, rAdvance]);

	/**
	 * Lifecycle hook on store state change. Hides loading animation on error message.
	 * Conditionally raises warning message (if not already raised) for users lacking Network access but attempting to acknowledge staff access agreement.
	 * Lack of access determined by failure to retrieve logged-on user information during initial loading.
	 *
	 * @function
	 * @return void. No clean-up performed.
	 */
	useEffect(() => {
		const WARNING_MESSAGE = (
			<span>
				Login credential unable to be determined. If attempting to
				acknowledge staff access agreement, click{' '}
				<a
					href='https://infonet.upmc.com/infonet/ls/content/5455391350667157/reminding-non-employees-staff-agreement-pdf-1764800026'
					rel='noopener noreferrer'
					target='_blank'
				>
					here
				</a>{' '}
				for manager instructions on resending.
			</span>
		);
		if (
			rLoading &&
			rMessages &&
			rMessages.length > 0 &&
			rMessages.some(message => message.type === 'Error')
		) {
			if (
				requestType === REQUEST_TYPES.SELF &&
				rMessages.some(
					message =>
						message.text === ERROR_MESSAGE_USER ||
						message.text === ERROR_MESSAGE_IMPERSONATOR ||
						message.text === ERROR_MESSAGE_IMPERSONATOR_UNKNOWN,
				) &&
				rMessages.every(message => message.text !== WARNING_MESSAGE)
			) {
				raiseWarningBanner(WARNING_MESSAGE, dispatch);
			}
			dispatch(
				init({
					loading: false,
				}),
			);
		}
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [rMessages]);

	/**
	 * Lifecycle hook on store state change. Monitors presence of selected requestee(s), tracks third-party organization association violations within multi-user requests,
	 * raises appropriate banners to communicate updated status following initial load.
	 *
	 * @function
	 * @return void. No clean-up performed.
	 */
	useEffect(() => {
		if (
			!rLoading &&
			user &&
			requestType !== REQUEST_TYPES.SELF &&
			requestType !== REQUEST_TYPES.SELF_SERVICE_ACCOUNT
		) {
			dispatch(clearMessages());

			const hasRequestees =
				rRequestees &&
				rRequestees.length > 0 &&
				rRequestees.some(requestee => requestee.isSelected === true);
			setHasRequestees(hasRequestees);
			if (hasRequestees) {
				if (isMultiUserRequest(requestType)) {
					const isVendorAssociation = rRequestees.some(requestee => {
						let val = requestee.isSelected;
						if (val) {
							val = Object.keys(requestee).includes(
								CELL_KEYS.UNASSOCIATED,
							);
						}
						return val;
					});
					setIsVendorAssociation(isVendorAssociation);
					if (!isVendorAssociation) {
						raiseInformationalBanner(
							'No further action required for currently selected requestees.',
							dispatch,
						);
					}
				}
			} else {
				raiseWarningBanner(
					'There are no currently selected requestees. Re-select one or more requestee(s) to continue.',
					dispatch,
				);
			}
		}
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [rLoading, rRequestees, user]);

	/**
	 * Submission lifecycle hook on store state change.
	 *
	 * @function
	 * @return void. No clean-up performed.
	 */
	useEffect(() => {
		/**
		 * Asynchronously handles form submission and routing.
		 *
		 * @function
		 * @return void.
		 */
		const advance = async () => {
			try {
				let location = null;
				if (isMultiUserRequest(requestType)) {
					const requestees = rRequestees
						.filter(requestee => requestee.isSelected === true)
						.map(r => r['FI_ID'].Value);
					location = getLegacyPath(
						'/RRS/MultiUser_RequestModifyView.aspx?ID=' +
							requestees[0],
					);

					await setSession('UsersList', requestees.join());

					if (sIsVendorAssociation) {
						const unassociatedRequestees = requestees.filter(
							requestee =>
								rRequestees.find(
									rRequestee =>
										rRequestee['FI_ID'].Value ===
											requestee &&
										rRequestee[CELL_KEYS.UNASSOCIATED] &&
										rRequestee[CELL_KEYS.UNASSOCIATED]
											.Value === 'false',
								),
						);
						await postSystemAccessAgreement(
							rForm,
							unassociatedRequestees,
							user.ID,
							sTrackingNumber,
							dispatch,
						).then(async () => {
							await setSession(
								'tpoAssociationUsersList',
								unassociatedRequestees.join(),
							);

							location =
								location +
								encodeURIComponent('&tn=' + sTrackingNumber);

							history.push(location);
						});
					} else {
						history.push(location);
					}
				} else {
					/**
					 * Requestee unique identifier. Defaults to requestor unique identifier if unavailable, i.e., new non-employee requests.
					 * @type {number}
					 */
					const requesteeID = Object.keys(rRequestees[0]).includes(
						'FI_ID',
					)
						? rRequestees[0]['FI_ID'].Value
						: user.ID;

					await setSession('RequestorESOFIID', user.ID.toString());
					await setSession('RequestorESOID', user.ESOID);
					await setSession(
						'RequesteeESOFIID',
						requesteeID.toString(),
					);
					await setSession('id', requesteeID.toString());
					if (
						!sIsStaffAccessAgreement &&
						!sIsTenantBadgeOnly &&
						!sIsRegulatoryUser &&
						!sIsVendorAssociation &&
						sIsComputerAccess
					) {
						if (
							requestType ===
								REQUEST_TYPES.SELF_SERVICE_ACCOUNT ||
							requestType ===
								REQUEST_TYPES.FOR_OTHERS_SERVICE_ACCOUNT
						) {
							history.push({
								pathname: '/ExistingServiceAccount',
								state: rRequestees[0],
							});
						} else {
							// no violations requiring processing
							history.push(
								getLegacyPath('/App/Index?ID=' + requesteeID),
								rRequestees[0],
							);
						}
					} else {
						if (sIsStaffAccessAgreement) {
							if (
								requestType ===
									REQUEST_TYPES.SELF_SERVICE_ACCOUNT ||
								requestType ===
									REQUEST_TYPES.FOR_OTHERS_SERVICE_ACCOUNT
							) {
								await postStaffAccessAgreementServiceAccount(
									sTrackingNumber,
									requesteeID,
									sIPAddress,
								).then(() => {
									history.push({
										pathname: '/ExistingServiceAccount',
										state: rRequestees[0],
									});
								});
							} else {
								await postStaffAccessAgreement(
									sTrackingNumber,
									requesteeID,
									sIPAddress,
								).then(() => {
									history.push(
										getLegacyPath(
											'/App/Index?ID=' + requesteeID,
										),
									);
								});
							}
						}
						if (sIsRegulatoryUser) {
							await postSurveyorGuidelines(sTrackingNumber).then(
								() => {
									history.push(getLegacyPath('/App/Index'));
								},
							);
						}
						if (sIsTenantBadgeOnly || !sIsComputerAccess) {
							if (requestType === REQUEST_TYPES.NON_EMPLOYEE) {
								await postNoComputerAccess(
									sTrackingNumber,
								).then(async () => {
									await setSession('ssnNoCompAccess', true);
									history.push(
										getLegacyPath(
											'/Confirm/Index?tn=' +
												sTrackingNumber,
										),
									);
								});
							} else {
								await postUserType(
									rForm,
									requesteeID,
									user.ID,
									sTrackingNumber,
								);
								// no redirect, handled as vendor association
							}
						}
						if (sIsVendorAssociation) {
							await postSystemAccessAgreement(
								rForm,
								[requesteeID],
								user.ID,
								sTrackingNumber,
								dispatch,
							).then(async () => {
								if (
									requestType ===
										REQUEST_TYPES.SELF_SERVICE_ACCOUNT ||
									requestType ===
										REQUEST_TYPES.FOR_OTHERS_SERVICE_ACCOUNT
								) {
									history.push({
										pathname: '/ExistingServiceAccount',
										state: rRequestees[0],
									});
								} else {
									if (
										userType?.toLowerCase() ===
										USER_TYPES.EPIC_LINK_USER
									) {
										await postAppAdd(sTrackingNumber, {
											Items: [
												{ ID: 1617, ActionType: 'Add' },
												{ ID: 23, ActionType: 'Add' },
											],
										});
										history.push(
											'/RRS/Request_ModifyDetail_View.aspx?tn=' +
												sTrackingNumber,
										);
									} else {
										history.push(
											getLegacyPath(
												'/App/Index' +
													(requestType ===
													REQUEST_TYPES.NON_EMPLOYEE
														? '?tn=' +
															sTrackingNumber
														: '?ID=' + requesteeID),
											),
										);
									}
								}
							});
						}
					}
				}
			} catch (error) {
				if (!error.message.includes('Success')) {
					raiseErrorBanner(
						error.cause ? error.cause : error,
						dispatch,
					);
				}
				dispatch(
					init({
						loading: false,
					}),
				);
			} finally {
				dispatch(
					init({
						advance: false,
					}),
				);
			}
		};
		if (rAdvance) {
			advance();
		}
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [rAdvance]);
	//#endregion

	//#region JSX
	/**
	 * Control for canceling request and redirecting home.
	 * @type {ReactElement}
	 */
	const actionButtonCancel = (
		<HomeButton
			onClick={() => {
				dispatch(init({}));
			}}
		>
			Cancel
		</HomeButton>
	);
	/**
	 * Control for advancing request. Available when no error messages raised.
	 * @type {ReactElement}
	 */
	const actionButtonProceed = (
		<Button disabled={rAdvance} variant='outlined' type='submit'>
			Proceed
		</Button>
	);
	//#endregion

	const multiselectHeaders = HEADERS.reduce((objHeaders, header) => {
		objHeaders[header.replace(/ /g, '_')] = header;
		return objHeaders;
	}, {});

	return (
		<WithLoadingHandler
			isLoading={!refMounted.current}
			showChildren={refMounted.current}
		>
			<Base
				helpLink='placeholder'
				loading={rLoading}
				title={'Agreements'}
			>
				<RequesteeSelections headers={multiselectHeaders} />
				<Divider className={classes[CSS_CLASSES.DIVIDER]} />
				<FormWrapper
					enableReinitialize
					initialValues={formikInitialValues}
					onSubmit={onSubmit}
					validationSchema={formikValidationSchema}
				>
					{({ formik }) => (
						<StyleAuxiliary
							classes={classes[CSS_CLASSES.FORM_BODY]}
						>
							{requestType === REQUEST_TYPES.NON_EMPLOYEE &&
								!sIsTenantBadgeOnly &&
								!sIsRegulatoryUser && (
									<Fragment>
										<Headline6
											className={
												classes[
													CSS_CLASSES.LABEL_RADIO_SET
												]
											}
										>
											Computer Access Is
										</Headline6>
										<RadioSet
											isVert
											changeHandler={e => {
												const isComputerAccess =
													e.target &&
													e.target.id ===
														FORM_KEYS.COMPUTER_ACCESS;
												setIsComputerAccess(
													isComputerAccess,
												);
												// control only displayed for non-employee vendor associations, permissible to toggle
												setIsVendorAssociation(
													isComputerAccess,
												);
											}}
											name={
												FORM_KEYS.COMPUTER_ACCESS_OPTIONS
											}
										>
											<Radio
												checked={sIsComputerAccess}
												id={FORM_KEYS.COMPUTER_ACCESS}
												key='0'
												label='Required'
												name='computer_access'
											/>
											<Radio
												checked={!sIsComputerAccess}
												id={
													FORM_KEYS.NO_COMPUTER_ACCESS
												}
												key='1'
												label='Not Required'
												name='computer_access'
											/>
										</RadioSet>
										{sIsComputerAccess !== null &&
											!sIsComputerAccess && (
												<Subtitle2
													className={
														classes[
															CSS_CLASSES
																.LABEL_WARNING_RADIO_SET
														]
													}
												>
													Account will only remain
													active for 60 days unless
													associated with an
													application or badge
													request.
												</Subtitle2>
											)}
									</Fragment>
								)}
							{sIsComputerAccess !== null &&
								sIsComputerAccess === true && (
									<Fragment>
										{sIsVendorAssociation && (
											<Fragment>
												<Headline5>
													Third-Party Organization
													Association
												</Headline5>
												<Subtitle2>
													Requestee(s) must be
													associated with a
													third-party organization who
													has signed the UPMC System
													Access Agreement.
												</Subtitle2>
												{isMultiUserRequest(
													requestType,
												) ? (
													<RequesteeSelections
														headers={
															multiselectHeaders
														}
														filter={requestee =>
															requestee[
																CELL_KEYS
																	.UNASSOCIATED
															] &&
															requestee[
																CELL_KEYS
																	.UNASSOCIATED
															].Value === 'false'
														}
														label={
															'Requestees To Associate'
														}
														isReadOnly={true}
														sticky={false}
													/>
												) : null}
												<OrganizationSearchBox
													helperText={
														<HelperText persistent>
															Creation of new
															organization can be
															initiated{' '}
															<Link
																to={{
																	pathname:
																		'/Utilities/ThirdPartyOrganization/Dashboard',
																	search: `?ACTION=ADD`,
																}}
																label='here'
															>
																here.
															</Link>
														</HelperText>
													}
													labelPosition={
														LABEL_POSITIONS.TOP
													}
													onChange={
														formik.handleChange
													}
													touched={
														formik.touched[
															ORGANIZATION_FORM_KEYS
																.ORGANIZATION_NAME
														]
													}
												/>
												<br />
												<Checkbox
													id={
														FORM_KEYS.SPONSOR_ACKNOWLEDGMENT
													}
													label='I acknowledge that (1) I am authorized to request computer accounts/permissions for this individual and (2) this individual requires accounts/permissions to these computer systems in order perform their assigned duties at UPMC. As the sponsor of this account, I confirm that no patient information or confidential information will be sent via the non-UPMC email account provided for the requestee while in their role in accordance with UPMC Confidential Information (HS-HR0736) and Electronic Mail, Messaging and Texting (HS-IS0147) policies.'
													onChange={e => {
														formik.handleChange({
															target: {
																name: FORM_KEYS.SPONSOR_ACKNOWLEDGMENT,
																value: e.target
																	.checked,
															},
														});
													}}
													required
												/>
											</Fragment>
										)}
										{sIsTenantBadgeOnly &&
											requestType !==
												REQUEST_TYPES.NON_EMPLOYEE && (
												<>
													<Headline5>
														User Type Update
													</Headline5>
													<Subtitle2>
														Requestee's user type
														must be updated and
														additional information
														provided in order to be
														provisioned computer
														access.
													</Subtitle2>
													<UserType
														excludeTypes={
															userTypeExclusions
														}
														labelPosition={
															LABEL_POSITIONS.TOP
														}
													/>
													<AlternateEmailTextBox />
													<DepartmentSearchBox
														handleChange={
															formik.handleChange
														}
														touched={
															formik.touched[
																FORM_KEYS
																	.DEPARTMENT_ID
															]
														}
														setDepartmentId={
															setDepartmentId
														}
													/>
													<SponsoringDepartmentDropdown
														departmentId={
															departmentId
														}
													/>
													<HospitalDropdown
														sx={{
															width: '99%',
															marginLeft: '12px',
															marginRight: '12px',
														}}
													/>
													<HomeAddress
														sx={{
															width: '99%',
															marginLeft: '12px',
															marginRight: '12px',
														}}
														handleChange={
															formik.handleChange
														}
														labelPosition={
															LABEL_POSITIONS.TOP
														}
														required={false}
													/>
												</>
											)}
										{sIsStaffAccessAgreement && (
											<Fragment>
												<Headline5>
													Staff Access Agreement
												</Headline5>
												<Subtitle2>
													Requestee must acknowledge
													agreement before computer
													access is provisioned.
												</Subtitle2>
												<StaffAccessAgreement
													name={
														STAFF_ACCESS_AGREEMENT_FORM_KEYS.ACKNOWLEDGMENT
													}
												/>
											</Fragment>
										)}
										{sIsRegulatoryUser && (
											<Fragment>
												<Headline5>
													Surveyor Guideline
													Acknowledgment
												</Headline5>
												<Subtitle2>
													As a Regulatory User, the
													requestee must adhere to
													computer access
													expectations.
												</Subtitle2>
												<SurveyorGuidelineAcknowledgment
													name={
														SURVEYOR_FORM_KEYS.ACKNOWLEDGMENT
													}
												/>
											</Fragment>
										)}
									</Fragment>
								)}
							<StyleAuxiliary
								classes={classes[CSS_CLASSES.FOOTER]}
							>
								<Actions>
									<ActionButtons>
										{rMessages.every(
											message => message.type !== 'Error',
										) && sHasRequestees ? (
											<Fragment>
												{actionButtonCancel}
												{actionButtonProceed}
											</Fragment>
										) : (
											actionButtonCancel
										)}
									</ActionButtons>
								</Actions>
							</StyleAuxiliary>
						</StyleAuxiliary>
					)}
				</FormWrapper>
			</Base>
		</WithLoadingHandler>
	);
};

export { Agreements };
